
import { getCommonPrintStyles, getHeaderHtml, getFooterHtml, getSignatureHtml } from "./printStyles";
import { downloadAsJPG, downloadAsPDF, createPrintDocument } from "./printingBridge";
import { toast } from "sonner";

const RECORDS_PER_PAGE = 12; // Changed from 14 to 12 as requested

interface ReportDataItem {
  name: string;
  value: number;
}

/**
 * Export report data as JPG with pagination support
 */
export const printReportDataPaginatedAsJPG = async (
  data: ReportDataItem[],
  reportTitle: string,
  startPage: number = 1,
  endPage: number = 1
) => {
  try {
    console.log(`Starting ${reportTitle} JPG download...`, { data: data.length, startPage, endPage });
    
    const loadingToast = toast.loading(`Generating ${reportTitle} JPG...`);
    
    const { content, filename } = generateReportContent(data, reportTitle, startPage, endPage);
    
    console.log('Starting JPG download with filename:', filename);
    
    await downloadAsJPG(content, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error(`Failed to download ${reportTitle}`);
      }
    });
    
    toast.dismiss(loadingToast);
    const pageText = startPage === endPage ? `page ${startPage}` : `pages ${startPage}-${endPage}`;
    toast.success(`${reportTitle} ${pageText} downloaded as JPG!`);
    
  } catch (error) {
    console.error(`Error downloading paginated ${reportTitle}:`, error);
    toast.error(`Failed to download ${reportTitle}`);
  }
};

/**
 * Export report data as PDF with pagination support
 */
export const printReportDataPaginatedAsPDF = async (
  data: ReportDataItem[],
  reportTitle: string,
  startPage: number = 1,
  endPage: number = 1
) => {
  try {
    console.log(`Starting ${reportTitle} PDF download...`, { data: data.length, startPage, endPage });
    
    const loadingToast = toast.loading(`Generating ${reportTitle} PDF...`);
    
    const { content, filename } = generateReportContent(data, reportTitle, startPage, endPage);
    
    console.log('Starting PDF download with filename:', filename);
    
    await downloadAsPDF(content, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error(`Failed to download ${reportTitle}`);
      }
    });
    
    toast.dismiss(loadingToast);
    const pageText = startPage === endPage ? `page ${startPage}` : `pages ${startPage}-${endPage}`;
    toast.success(`${reportTitle} ${pageText} downloaded as PDF!`);
    
  } catch (error) {
    console.error(`Error downloading paginated ${reportTitle}:`, error);
    toast.error(`Failed to download ${reportTitle}`);
  }
};

const generateReportContent = (data: ReportDataItem[], reportTitle: string, startPage: number, endPage: number) => {
  const totalPages = Math.ceil(data.length / RECORDS_PER_PAGE);
  const actualEndPage = Math.min(endPage, totalPages);
  const actualStartPage = Math.max(1, startPage);
  
  console.log('Calculated pages:', { totalPages, actualStartPage, actualEndPage });
  
  // Calculate which records to include
  const startIndex = (actualStartPage - 1) * RECORDS_PER_PAGE;
  const endIndex = actualEndPage * RECORDS_PER_PAGE;
  const recordsToShow = data.slice(startIndex, endIndex);
  
  console.log('Records to show:', { startIndex, endIndex, count: recordsToShow.length });
  
  // Group records by pages
  const pages: ReportDataItem[][] = [];
  for (let i = 0; i < recordsToShow.length; i += RECORDS_PER_PAGE) {
    pages.push(recordsToShow.slice(i, i + RECORDS_PER_PAGE));
  }
  
  // Calculate total for the entire dataset
  const grandTotal = data.reduce((sum, item) => sum + item.value, 0);
  
  // Generate content for all pages using standardized structure
  const allPagesContent = pages.map((pageRecords, pageIndex) => {
    const currentPageNumber = actualStartPage + pageIndex;
    
    return `
      <div class="print-page ${pageIndex > 0 ? 'page-break' : ''}">
        ${getHeaderHtml(`${reportTitle} - Page ${currentPageNumber} of ${totalPages}`)}
        
        <div class="print-content">
          <div class="page-info">
            <strong>Page ${currentPageNumber} of ${totalPages}</strong> | 
            Records ${(currentPageNumber - 1) * RECORDS_PER_PAGE + 1} - ${Math.min(currentPageNumber * RECORDS_PER_PAGE, data.length)} of ${data.length}
          </div>
          
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Category</th>
                <th>Value</th>
                <th>Percentage</th>
              </tr>
            </thead>
            <tbody>
              ${pageRecords.map((record, index) => {
                const displayId = (startIndex + (pageIndex * RECORDS_PER_PAGE) + index + 1).toString();
                const percentage = grandTotal > 0 ? ((record.value / grandTotal) * 100).toFixed(1) : '0.0';
                
                return `
                  <tr>
                    <td>${displayId}</td>
                    <td>${record.name || 'N/A'}</td>
                    <td>${record.value.toLocaleString()}</td>
                    <td>${percentage}%</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          ${pageIndex === pages.length - 1 ? `
            <div class="summary">
              Total Records: ${data.length} | Downloaded Pages: ${pages.length}/${totalPages} | Grand Total: ${grandTotal.toLocaleString()}
            </div>
          ` : ''}
          
          ${getSignatureHtml('report')}
        </div>
        
        ${getFooterHtml('report')}
      </div>
    `;
  }).join('');

  const styles = `
    ${getCommonPrintStyles()}
    .page-info {
      text-align: center;
      font-size: 0.9em;
      color: #666;
      margin: 10px 0;
      padding: 8px;
      background: #f8f9fa;
      border-radius: 4px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 16px;
      font-size: 0.85em;
    }
    th, td {
      padding: 8px 6px;
      border-bottom: 1px solid #ddd;
      text-align: left;
      vertical-align: top;
    }
    th {
      background: #f5f5f5;
      font-weight: bold;
      border-bottom: 2px solid #ccc;
      font-size: 0.9em;
    }
    .summary {
      margin-top: 20px;
      font-weight: bold;
      font-size: 1.1em;
      text-align: center;
      padding: 10px;
      background: #f8f9fa;
      border-radius: 4px;
    }
  `;

  // Generate the complete print document
  const printDocument = createPrintDocument(allPagesContent, `${reportTitle}_Report_Paginated`, styles);
  
  // Create filename
  const filename = actualStartPage === actualEndPage 
    ? `${reportTitle.replace(/\s+/g, '_')}_Report_Page_${actualStartPage}_${new Date().toISOString().split('T')[0]}`
    : `${reportTitle.replace(/\s+/g, '_')}_Report_Pages_${actualStartPage}-${actualEndPage}_${new Date().toISOString().split('T')[0]}`;
  
  return { content: printDocument, filename };
};
